import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../App";
import { styledToast as toast } from "@/components/ui/sonner";
import { Card } from "@/components/ui/card";
import { LoadingOverlay } from "../components/LoadingOverlay";
import { Button } from "@/components/ui/button";
import ApplicationCard from "../components/ApplicationCard";
import NotificationBell from "../components/NotificationBell";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { motion } from "framer-motion";
import { Plus, LogOut, FileText, CheckCircle, Clock, XCircle, Play, X, Loader2, RefreshCw, BookOpen } from "lucide-react";
import { useTranslation } from 'react-i18next';

export default function FarmerDashboard({ user }) {
  const { t, i18n } = useTranslation();
  const [applications, setApplications] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [audioState, setAudioState] = useState({
    status: 'idle', // idle, generating, completed, failed
    audioUrl: null,
    script: null,
    error: null
  });
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [appsRes, statsRes] = await Promise.all([
        api.get("/applications"),
        api.get("/dashboard/stats"),
      ]);
      setApplications(appsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error(t('common.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login");
  };

  // Get the most recent application for personalized video
  const getLatestApplication = useCallback(() => {
    if (applications.length > 0) {
      return applications[0]; // Assuming sorted by date desc
    }
    return null;
  }, [applications]);

  // Generate personalized MLA audio message
  const generateAudio = async () => {
    const latestApp = getLatestApplication();
    
    setAudioState({
      status: 'generating',
      audioUrl: null,
      script: null,
      error: null
    });

    try {
      const language = i18n.language === 'kn' ? 'kannada' : 'english';
      
      const response = await api.post('/tts/generate-audio', {
        farmer_name: user?.name || 'Farmer',
        application_purpose: latestApp?.purpose || t('mla.defaultPurpose'),
        application_status: latestApp?.status || 'submitted',
        language: language
      });

      if (response.data.status === 'completed' && response.data.audio_id) {
        // Use the direct audio URL from backend
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const audioUrl = `${backendUrl}/api/tts/audio/${response.data.audio_id}`;
        
        setAudioState({
          status: 'completed',
          audioUrl: audioUrl,
          script: response.data.script,
          error: null
        });
      } else {
        throw new Error('Audio generation failed');
      }
      
    } catch (error) {
      console.error('Error generating audio:', error);
      setAudioState({
        status: 'failed',
        audioUrl: null,
        script: null,
        error: error.response?.data?.detail || t('mla.audioError')
      });
    }
  };

  // Handle opening video modal
  const handleOpenVideoModal = () => {
    setShowVideoModal(true);
    // Auto-generate audio if not already generated
    if (audioState.status === 'idle') {
      generateAudio();
    }
  };

  // Reset audio state when modal closes
  const handleCloseVideoModal = () => {
    setShowVideoModal(false);
  };

  if (loading) {
    return <LoadingOverlay />;
  }

  return (
    <div className="min-h-screen bg-stone-100 pb-16 md:pb-0">
      <header className="bg-white border-b border-stone-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex justify-between items-center">
            <div className="min-w-0 flex-1">
              <h1 className="text-lg sm:text-2xl font-manrope font-bold text-emerald-700 tracking-tight truncate">
                {t('appName')}
              </h1>
              <p className="text-xs sm:text-sm text-slate-700 truncate">{t('dashboard.welcomeBack')}, {user?.name}</p>
            </div>
            <div className="flex items-center gap-1 sm:gap-2 ml-2">
              <LanguageSwitcher />
              <NotificationBell />
              <Button
                variant="outline"
                onClick={() => navigate("/help")}
                className="border-emerald-500 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 hover:text-emerald-800 px-2 sm:px-4 font-semibold"
                data-testid="help-button"
              >
                <BookOpen className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">{t('common.help') || 'Help'}</span>
              </Button>
              <Button
                variant="ghost"
                onClick={handleLogout}
                className="hover:bg-stone-100 text-slate-600 px-2 sm:px-4"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">{t('dashboard.logout')}</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-md mx-auto md:max-w-6xl px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* MLA Message Section */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 sm:mb-8"
        >
          <Card className="p-4 sm:p-6 bg-gradient-to-r from-emerald-800 to-emerald-700 border-none shadow-lg overflow-hidden" data-testid="mla-message-card">
            <div className="flex flex-col md:flex-row items-center gap-4 sm:gap-6">
              {/* MLA Photo/Video Placeholder */}
              <div className="relative flex-shrink-0">
                <div className="w-32 h-32 md:w-40 md:h-40 rounded-full bg-white/20 flex items-center justify-center overflow-hidden border-4 border-white/30 shadow-xl">
                  <img
                    src="https://customer-assets.emergentagent.com/job_farmer-track/artifacts/ifcwhvq9_MLAPhoto.jpg"
                    alt="MLA Balakrishna"
                    className="w-full h-full object-cover"
                    data-testid="mla-image"
                  />
                </div>
                <div 
                  className="absolute bottom-0 right-0 bg-white rounded-full p-2 shadow-lg cursor-pointer hover:bg-emerald-50 transition-colors" 
                  data-testid="mla-video-button"
                  onClick={handleOpenVideoModal}
                >
                  <Play className="w-5 h-5 text-emerald-800 fill-emerald-800" />
                </div>
              </div>
              {/* MLA Message Content */}
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-xl md:text-2xl font-manrope font-bold text-white mb-2">
                  {t('mla.name')}
                </h3>
                <p className="text-emerald-100 text-sm mb-3">
                  {t('mla.title')}
                </p>
                <p className="text-white/90 text-sm md:text-base leading-relaxed">
                  {t('mla.message')}
                </p>
              </div>
            </div>
          </Card>
        </motion.div>

        {stats && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4 mb-6 sm:mb-8"
          >
            <Card className="p-3 sm:p-5 bg-white border border-stone-200 shadow-sm" data-testid="stat-total">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-slate-100 rounded-lg">
                  <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-slate-700" />
                </div>
                <div>
                  <p className="text-xl sm:text-2xl font-manrope font-bold text-slate-900">{stats.total_applications}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">{t('stats.total')}</p>
                </div>
              </div>
            </Card>
            <Card className="p-3 sm:p-5 bg-white border border-stone-200 shadow-sm" data-testid="stat-pending">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-blue-100 rounded-lg">
                  <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-blue-700" />
                </div>
                <div>
                  <p className="text-xl sm:text-2xl font-manrope font-bold text-slate-900">{stats.pending}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">{t('stats.pending')}</p>
                </div>
              </div>
            </Card>
            <Card className="p-3 sm:p-5 bg-white border border-stone-200 shadow-sm" data-testid="stat-approved">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-emerald-100 rounded-lg">
                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-700" />
                </div>
                <div>
                  <p className="text-xl sm:text-2xl font-manrope font-bold text-slate-900">{stats.approved}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">{t('stats.approved')}</p>
                </div>
              </div>
            </Card>
            <Card className="p-3 sm:p-5 bg-white border border-stone-200 shadow-sm" data-testid="stat-rejected">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-red-100 rounded-lg">
                  <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-700" />
                </div>
                <div>
                  <p className="text-xl sm:text-2xl font-manrope font-bold text-slate-900">{stats.rejected}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">{t('stats.rejected')}</p>
                </div>
              </div>
            </Card>
          </motion.div>
        )}

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0 mb-4 sm:mb-6">
          <h2 className="text-xl sm:text-2xl font-manrope font-bold text-emerald-700 tracking-tight">
            {t('dashboard.myApplications')}
          </h2>
          <Button
            onClick={() => navigate("/farmer/new-application")}
            className="bg-emerald-800 hover:bg-emerald-900 text-white shadow-sm transition-all active:scale-95 w-full sm:w-auto"
            data-testid="new-application-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('dashboard.newApplication')}
          </Button>
        </div>

        {applications.length === 0 ? (
          <Card className="p-8 sm:p-12 bg-white border border-stone-200 text-center">
            <FileText className="w-10 h-10 sm:w-12 sm:h-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-base sm:text-lg font-manrope font-semibold text-slate-900 mb-2">
              {t('dashboard.noApplications')}
            </h3>
            <p className="text-sm sm:text-base text-slate-500 mb-4">
              {t('dashboard.createFirst')}
            </p>
            <Button
              onClick={() => navigate("/farmer/new-application")}
              className="bg-emerald-800 hover:bg-emerald-900 text-white w-full sm:w-auto"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('dashboard.createApplication')}
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-testid="applications-grid">
            {applications.map((app) => (
              <ApplicationCard
                key={app.id}
                application={app}
                onClick={() => navigate(`/application/${app.id}`)}
              />
            ))}
          </div>
        )}
      </main>

      {/* MLA Audio Message Modal */}
      {showVideoModal && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={handleCloseVideoModal}
          data-testid="video-modal-overlay"
        >
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="relative w-full max-w-2xl bg-slate-900 rounded-xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={handleCloseVideoModal}
              className="absolute top-4 right-4 z-10 bg-white/10 hover:bg-white/20 rounded-full p-2 transition-colors"
              data-testid="video-modal-close"
            >
              <X className="w-6 h-6 text-white" />
            </button>

            {/* Header */}
            <div className="bg-emerald-800 p-4">
              <h3 className="text-xl font-manrope font-bold text-white">
                {t('mla.videoTitle')}
              </h3>
              <p className="text-emerald-100 text-sm">
                {t('mla.personalizedFor')} {user?.name}
              </p>
            </div>

            {/* Content Area */}
            <div className="bg-gradient-to-b from-slate-800 to-slate-900 p-8">
              {/* Generating State */}
              {audioState.status === 'generating' && (
                <div className="flex flex-col items-center justify-center py-8">
                  <div className="relative">
                    <img
                      src="https://customer-assets.emergentagent.com/job_farmer-track/artifacts/ifcwhvq9_MLAPhoto.jpg"
                      alt="MLA Balakrishna"
                      className="w-40 h-40 rounded-full object-cover border-4 border-emerald-500/50 opacity-50"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Loader2 className="w-12 h-12 text-emerald-400 animate-spin" />
                    </div>
                  </div>
                  <p className="text-white text-lg font-medium text-center mt-6 mb-2">
                    {t('mla.generatingAudio')}
                  </p>
                  <p className="text-slate-400 text-sm text-center max-w-md">
                    {t('mla.generatingAudioMessage')}
                  </p>
                </div>
              )}

              {/* Completed State - Show Image with Audio */}
              {audioState.status === 'completed' && audioState.audioUrl && (
                <div className="flex flex-col items-center justify-center py-4">
                  <div className="relative mb-6">
                    <img
                      src="https://customer-assets.emergentagent.com/job_farmer-track/artifacts/ifcwhvq9_MLAPhoto.jpg"
                      alt="MLA Balakrishna"
                      className="w-48 h-48 rounded-full object-cover border-4 border-emerald-500 shadow-xl"
                    />
                    <div className="absolute -bottom-2 -right-2 bg-emerald-500 rounded-full p-3 shadow-lg">
                      <Play className="w-6 h-6 text-white fill-white" />
                    </div>
                  </div>
                  <h4 className="text-white text-xl font-semibold mb-1">{t('mla.name')}</h4>
                  <p className="text-emerald-400 text-sm mb-4">{t('mla.title')}</p>
                  
                  {/* Audio Player */}
                  <div className="w-full max-w-md bg-slate-700 rounded-lg p-4">
                    <audio
                      controls
                      className="w-full h-12"
                      src={audioState.audioUrl}
                      data-testid="mla-audio-player"
                      style={{ backgroundColor: 'transparent' }}
                    />
                  </div>
                  
                  {/* Script/Transcript */}
                  {audioState.script && (
                    <div className="w-full max-w-md mt-4 bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                      <p className="text-xs text-slate-400 uppercase tracking-wide mb-2 font-medium">
                        {t('mla.transcript')}
                      </p>
                      <p className="text-slate-200 text-sm leading-relaxed italic">
                        &ldquo;{audioState.script}&rdquo;
                      </p>
                    </div>
                  )}
                  
                </div>
              )}

              {/* Failed State */}
              {audioState.status === 'failed' && (
                <div className="flex flex-col items-center justify-center py-8">
                  <XCircle className="w-16 h-16 text-red-400 mb-6" />
                  <p className="text-white text-lg font-medium text-center mb-2">
                    {t('mla.audioFailed')}
                  </p>
                  <p className="text-slate-400 text-sm text-center max-w-md mb-6">
                    {audioState.error || t('mla.audioError')}
                  </p>
                  <Button
                    onClick={generateAudio}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    {t('mla.retryAudio')}
                  </Button>
                </div>
              )}

              {/* Idle State (fallback) */}
              {audioState.status === 'idle' && (
                <div className="flex flex-col items-center justify-center py-8">
                  <img
                    src="https://customer-assets.emergentagent.com/job_farmer-track/artifacts/ifcwhvq9_MLAPhoto.jpg"
                    alt="MLA Balakrishna"
                    className="w-40 h-40 rounded-full object-cover border-4 border-emerald-500/50 mb-6"
                  />
                  <Button
                    onClick={generateAudio}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    {t('mla.playMessage')}
                  </Button>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}